import type { FC, ReactNode } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AnswerSectionHeading } from "../chat-components/answer-section-heading";
import { ANIMATION_SPEEDS, spacing, transitions } from "../design-tokens";

type AnimatedSectionProps = {
  isVisible?: boolean;
  heading?: string;
  children: ReactNode;
  gap?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  animationType?: "fadeSlide" | "fadeOnly" | "none";
  className?: string;
};

const Container = styled.div<{
  isVisible: boolean;
  animationType: "fadeSlide" | "fadeOnly" | "none";
}>`
  ${(props) => {
    if (props.animationType === "none") {
      return css`
        display: ${props.isVisible ? "block" : "none"};
      `;
    }

    if (props.animationType === "fadeOnly") {
      return css`
        ${transitions.fadeOnly()}
        opacity: ${props.isVisible ? 1 : 0};
        pointer-events: ${props.isVisible ? "auto" : "none"};
        max-height: 800px;
      `;
    }

    // fadeSlide animation
    return css`
      ${transitions.fadeSlide()}
      max-height: ${props.isVisible ? "800px" : "0"};
      opacity: ${props.isVisible ? 1 : 0};
      transform: translateY(${props.isVisible ? 0 : 8}px);
      overflow: hidden;
      transition: opacity ${ANIMATION_SPEEDS.STANDARD}ms ease-in-out, transform ${ANIMATION_SPEEDS.STANDARD}ms ease-in-out, max-height ${ANIMATION_SPEEDS.STANDARD}ms ease-in-out;

      ${!props.isVisible && css`
        padding: 0;
        margin: 0;
      `}
    `;
  }}
`;

const ContentWrapper = styled.div<{ gap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.gap ? spacing[props.gap as keyof typeof spacing] : 0}px;
`;

export const AnimatedSection: FC<AnimatedSectionProps> = ({
  isVisible = true,
  heading,
  children,
  gap,
  animationType = "fadeSlide",
  className,
}) => {
  return (
    <Container
      isVisible={isVisible}
      animationType={animationType}
      className={className}
    >
      <ContentWrapper gap={gap}>
        {heading && <AnswerSectionHeading>{heading}</AnswerSectionHeading>}
        {children}
      </ContentWrapper>
    </Container>
  );
};
